<template>
  <HeadlessMultipleSetting
    :settingScheme="{ brand_slider: brandSliderScheme }"
    v-slot="{ formData, save, loading, isActive }"
  >
    <SettingModal
      :isActive="isActive"
      :save="save"
      :loading="loading"
      :baseSettingsItem="baseSettingsItem"
    >
      <template #content="{ hide }">
        <form id="BrandSliderForm" novalidate @submit.prevent="onSubmit($event, save, hide)">
          <FormGeneretor
            :scheme="brandSliderScheme"
            schemeName="brand_slider"
            :value="formData('brand_slider')"
            key="brand_slider"
            form="BrandSlider"
            as="div"
            class="my-4"
            :components="$options.formGeneretorComponents"
          />
        </form>
        <div class="d-flex justify-content-end tw-items-end">
          <button
            type="button"
            class="bttn bttn--sm bttn--blue tw-me-auto tw-mt-7"
            @click="hide"
            name="button"
          >
            {{ $t("edit_campaign.setting_close", "Close") }}
          </button>
          <b-btn
            class="bttn bttn--sm bttn--orange"
            form="BrandSliderForm"
            type="submit"
            :disabled="loading"
            :style="{ gap: '10px' }"
          >
            <b-spinner v-if="loading" small></b-spinner>
            {{ $t("edit_campaign.setting_save", "Save") }}
          </b-btn>
        </div>
      </template>
    </SettingModal>
  </HeadlessMultipleSetting>
</template>

<script>
import { mapState } from "vuex";
import FormGeneretor from "@/common-components/form-generetor/form-generetor";
import HeadlessMultipleSetting from "@/views/campaigns/campaign-settings/headless-multiple-setting.vue";
import SettingModal from "@/views/campaigns/campaign-settings/settingModal.vue";

export default {
  formGeneretorComponents: {},
  components: {
    FormGeneretor,
    HeadlessMultipleSetting,
    SettingModal,
  },
  props: {
    baseSettingsItem: {
      type: Object,
      default: () => null,
    },
  },
  methods: {
    async onSubmit(form, save, hide) {
      await save(null, hide);
    },
  },
  computed: {
    ...mapState({
      campaignId: (state) => state.campaign.campaign.data.id,
    }),
    brandSliderHrefField() {
      return {
        title: this.$t("edit_campaign.schema.brand_slider.href_title", "URL"),
        type: "string",
        default: "",
        maxLength: 256,
        description: this.$t(
          "edit_campaign.schema.brand_slider.href_description",
          "Opens in a new tab when the image is clicked. Tip: to open the donation form with an amount filled in, use donate.charidy.com/{campaignId}?amount=360.",
          { campaignId: this.campaignId || "<campaign ID>" }
        ),
      };
    },
    brandSliderScheme() {
      return {
        title: "BrandSlider",
        type: "object",
        noValidateChild: true,
        properties: {
          value: {
            title: "Active",
            type: "boolean",
            default: false,
            expanded: true,
          },
          unify_desktop_and_mobile: {
            title: this.$t(
              "edit_campaign.schema.brand_slider.unify_desktop_and_mobile",
              "Unify desktop and mobile images"
            ),
            type: "boolean",
            default: false,
            description: this.$t(
              "edit_campaign.schema.brand_slider.unify_desktop_and_mobile_description",
              {
                returnEmptyOnMissing: true,
              }
            ),
          },
          show_projector: {
            title: this.$t(
              "edit_campaign.schema_show_projector",
              "Show the slider in projector mode"
            ),
            type: "boolean",
            default: false,
            description: this.$t("edit_campaign.schema_show_projector_description", {
              returnEmptyOnMissing: true,
            }),
          },
          autoplay_speed: {
            title: "Autoplay Speed (ms)",
            type: "number",
            default: 3000,
            description:
              "Time in milliseconds each slide is displayed before auto-advancing. Default: 3000",
          },
          speed: {
            title: "Transition Speed (ms)",
            type: "number",
            default: 300,
            description: "Duration in milliseconds of the slide transition animation. Default: 300",
          },
          slides_count: {
            title: "Slides Count",
            type: "object",
            noValidateChild: true,
            description: "Number of slides visible at once per screen size",
            properties: {
              desktop: {
                title: "Desktop",
                type: "number",
                default: 4,
              },
              mobile: {
                title: "Mobile",
                type: "number",
                default: 3,
              },
              small: {
                title: "Small",
                type: "number",
                default: 2,
              },
            },
          },
          desktop_unidy_on_top: {
            title: this.$t(
              "edit_campaign.brand_slider_setting_title_desktop_unidy_on_top",
              "desktop_unidy_on_top"
            ),
            tooltipText: this.$t(
              "edit_campaign.brand_slider_setting_tooltip_desktop_unidy_on_top",
              "desktop_unidy_on_top are unidy template on top images used for desktop and tablet devices"
            ),
            type: "list-card",
            default: [],
            items: {
              type: "object",
              properties: {
                image_src: {
                  title: "Image File",
                  type: "image",
                  required: true,
                  multiple: true,
                  preview: true,
                  allowAnyAspectRatio: true,
                },
                title: {
                  title: "Title",
                  type: "string",
                  default: "",
                  maxLength: 256,
                },
                href: this.brandSliderHrefField,
              },
            },
          },
          mobile_unidy_on_top: {
            title: this.$t(
              "edit_campaign.brand_slider_setting_title_mobile_unidy_on_top",
              "mobile_unidy_on_top"
            ),
            tooltipText: this.$t(
              "edit_campaign.brand_slider_setting_tooltip_mobile_unidy_on_top",
              "mobile_unidy_on_top are unidy template on top images used for mobile devices"
            ),
            type: "list-card",
            default: [],
            items: {
              type: "object",
              properties: {
                image_src: {
                  title: "Image File",
                  type: "image",
                  required: true,
                  preview: true,
                  multiple: true,
                  allowAnyAspectRatio: true,
                },
                title: {
                  title: "Title",
                  type: "string",
                  default: "",
                  maxLength: 256,
                },
                href: this.brandSliderHrefField,
              },
            },
          },
          desktop: {
            title: this.$t("edit_campaign.brand_slider_setting_title_desktop", "Desktop Images"),
            tooltipText: this.$t(
              "edit_campaign.brand_slider_setting_tooltip_desktop",
              "Desktop images are used for desktop and tablet devices"
            ),
            type: "list-card",
            default: [],
            items: {
              type: "object",
              properties: {
                image_src: {
                  title: "Image File",
                  type: "image",
                  required: true,
                  multiple: true,
                  preview: true,
                  allowAnyAspectRatio: true,
                },
                title: {
                  title: "Title",
                  type: "string",
                  default: "",
                  maxLength: 256,
                },
                href: this.brandSliderHrefField,
              },
            },
          },
          mobile: {
            title: this.$t("edit_campaign.brand_slider_setting_title_mobile", "Mobile Images"),
            tooltipText: this.$t(
              "edit_campaign.brand_slider_setting_tooltip_mobile",
              "Mobile images are used for mobile devices"
            ),
            type: "list-card",
            default: [],
            items: {
              type: "object",
              properties: {
                image_src: {
                  title: "Image File",
                  type: "image",
                  required: true,
                  multiple: true,
                  preview: true,
                  allowAnyAspectRatio: true,
                },
                title: {
                  title: "Title",
                  type: "string",
                  default: "",
                  maxLength: 256,
                },
                href: this.brandSliderHrefField,
              },
            },
          },
          desktop2: {
            title: this.$t("edit_campaign.brand_slider_setting_title_desktop2", "Desktop2 Images"),
            tooltipText: this.$t(
              "edit_campaign.brand_slider_setting_tooltip_desktop2",
              "Desktop2 are alternative images used for desktop and tablet devices"
            ),
            type: "list-card",
            default: [],
            items: {
              type: "object",
              properties: {
                image_src: {
                  title: "Image File",
                  type: "image",
                  required: true,
                  preview: true,
                  allowAnyAspectRatio: true,
                },
                title: {
                  title: "Title",
                  type: "string",
                  default: "",
                  maxLength: 256,
                },
                href: this.brandSliderHrefField,
              },
            },
          },
          mobile2: {
            title: this.$t("edit_campaign.brand_slider_setting_title_mobile2", "Mobile2 Images"),
            tooltipText: this.$t(
              "edit_campaign.brand_slider_setting_tooltip_mobile2",
              "Mobile2 are alternative images used for mobile devices"
            ),
            type: "list-card",
            default: [],
            items: {
              type: "object",
              properties: {
                image_src: {
                  title: "Image File",
                  type: "image",
                  required: true,
                  preview: true,
                  allowAnyAspectRatio: true,
                },
                title: {
                  title: "Title",
                  type: "string",
                  default: "",
                  maxLength: 256,
                },
                href: this.brandSliderHrefField,
              },
            },
          },
          desktop_onboarding: {
            title: this.$t(
              "edit_campaign.brand_slider_setting_title_desktop_onboarding",
              "desktop_onboarding"
            ),
            tooltipText: this.$t(
              "edit_campaign.brand_slider_setting_tooltip_desktop_onboarding",
              "desktop_onboarding are campaign onboarding images used for desktop and tablet devices"
            ),
            type: "list-card",
            default: [],
            items: {
              type: "object",
              properties: {
                image_src: {
                  title: "Image File",
                  type: "image",
                  required: true,
                  preview: true,
                  allowAnyAspectRatio: true,
                },
                title: {
                  title: "Title",
                  type: "string",
                  default: "",
                  maxLength: 256,
                },
                href: this.brandSliderHrefField,
              },
            },
          },
          mobile_onboarding: {
            title: this.$t(
              "edit_campaign.brand_slider_setting_title_mobile_onboarding",
              "mobile_onboarding"
            ),
            tooltipText: this.$t(
              "edit_campaign.brand_slider_setting_tooltip_mobile_onboarding",
              "mobile_onboarding are campaign onboarding images used for mobile devices"
            ),
            type: "list-card",
            default: [],
            items: {
              type: "object",
              properties: {
                image_src: {
                  title: "Image File",
                  type: "image",
                  required: true,
                  preview: true,
                  allowAnyAspectRatio: true,
                },
                title: {
                  title: "Title",
                  type: "string",
                  default: "",
                  maxLength: 256,
                },
                href: this.brandSliderHrefField,
              },
            },
          },
          desktop_countdown: {
            title: this.$t(
              "edit_campaign.brand_slider_setting_title_desktop_coundown",
              "desktop_coundown"
            ),
            tooltipText: this.$t(
              "edit_campaign.brand_slider_setting_tooltip_desktop_coundown",
              "desktop_coundown are campaign coundown images"
            ),
            type: "list-card",
            default: [],
            items: {
              type: "object",
              properties: {
                image_src: {
                  title: "Image File",
                  type: "image",
                  required: true,
                  preview: true,
                  allowAnyAspectRatio: true,
                },
                title: {
                  title: "Title",
                  type: "string",
                  default: "",
                  maxLength: 256,
                },
                href: this.brandSliderHrefField,
              },
            },
          },
          desktop_countdown_on_top: {
            title: this.$t(
              "edit_campaign.brand_slider_setting_title_desktop_coundown_on_top",
              "desktop_coundown_on_top"
            ),
            tooltipText: this.$t(
              "edit_campaign.brand_slider_setting_tooltip_desktop_coundown_on_top",
              "desktop_coundown_on_top are campaign coundown images displayed on the top of the campaign page"
            ),
            type: "list-card",
            default: [],
            items: {
              type: "object",
              properties: {
                image_src: {
                  title: "Image File",
                  type: "image",
                  required: true,
                  preview: true,
                  allowAnyAspectRatio: true,
                },
                title: {
                  title: "Title",
                  type: "string",
                  default: "",
                  maxLength: 256,
                },
                href: this.brandSliderHrefField,
              },
            },
          },
        },
      };
    },
  },
};
</script>
